import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from '../../../api/useFetch';
import { API_GET, API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { hasPermissions } from '../../../auth/hasPermissions';
import ExoTable from '../../../components/exo/ExoTable';
import ExoDialog from '../../../components/exo/ExoDialog';
import ExoAlert from '../../../components/exo/ExoAlert';
import MoneyLabel from '../../../components/label/MoneyLabel';
import DateLabel from '../../../components/label/DateLabel';
import Pagination from '../../../components/special/Pagination';
import { Status } from '../../../components/special/Status';
import { numberToString } from '../../../components/special/numberConverter';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import RequestPageIcon from '@mui/icons-material/RequestPage';
import CardGrid from '../../../components/cards/layout/CardGrid';
import CardContainer from '../../../components/cards/layout/CardContainer';
import FilePreviewCard from '../../../components/cards/FilePreviewCard';
import { TravelExpenseRequestDetails } from '../../new-requests/extended-request-versions/ExtendTravelExpense';
import NewTravelExpense from '../../new-requests/NewTravelExpense';
import { MeansOfTransportationIcon } from '../../new-requests/MeansOfTransportationIcon';
import { tokens } from '../../../global/theme/tokens';

export const EmployeeTravelExpenseReimbursements = ({ employeeInfo, own }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const userId = employeeInfo?.id;

  // Personnel managers may correct/remove individual travel expenses. The trip is
  // a first-class record, so editing patches it in place; deleting declines the
  // originating request, which marks the trip rather than destroying it.
  const canManage = hasPermissions('employee-request-management', 'write');

  const { apiData, isLoading, reloadApiData } = useFetch(
    own
      ? `travel-expense-reimbursements/own?sortBy=startDate&sortOrder=desc&perPage=10`
      : `travel-expense-reimbursements?userId[eq]=${userId}&sortBy=startDate&sortOrder=desc&perPage=10`
  );

  const {
    apiData: unreimbursed,
    setApiData: setUnreimbursed,
    reloadApiData: reloadUnreimbursed,
  } = useFetch(
    own
      ? `travel-expenses/own?unreimbursed=1&status[ne]=3&perPage=10`
      : `travel-expenses?userId[eq]=${userId}&unreimbursed=1&status[ne]=3&perPage=10`
  );

  const [unreimbursedLoading, setUnreimbursedLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editExpense, setEditExpense] = useState(null);
  const [deleteExpense, setDeleteExpense] = useState(null);

  function reloadAll() {
    reloadApiData();
    reloadUnreimbursed();
  }

  function handleEditDone() {
    setEditExpense(null);
    reloadAll();
  }

  function handleCreateDone() {
    setCreateOpen(false);
    reloadAll();
  }

  // The list only carries the trip's summary columns. Editing needs its legs,
  // day rows and cost receipts, which only the detail endpoint returns.
  function handleEdit(expense) {
    DBRequest({
      config,
      path: `travel-expenses/${expense.id}`,
      method: API_GET,
      onResponse: (response) => setEditExpense(response.data),
    });
  }

  function handleDelete() {
    if (!deleteExpense?.employeeRequestId) return;
    DBRequest({
      config,
      path: `employeerequests/${deleteExpense.employeeRequestId}`,
      method: API_PATCH({ status: 2 }),
      onResponse: () => {
        setDeleteExpense(null);
        reloadAll();
      },
    });
  }

  // An entry can be corrected only while it is still tied to an editable
  // request and not yet locked into a generated reimbursement.
  const isEditable = (expense) =>
    canManage &&
    Boolean(expense.employeeRequestId) &&
    !expense.travelExpenseReimbursementId &&
    expense.status !== 'declined';

  return (
    <Box className="w-full flex flex-col gap-4">
      <Box className="flex flex-col gap-1">
        <Box className="flex items-center gap-1">
          <Typography fontWeight={600}>{t('Not yet reimbursed')}</Typography>
          {canManage && (
            <Tooltip title={t('New Travel Expense')}>
              <IconButton size="small" onClick={() => setCreateOpen(true)}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {!unreimbursed ? (
          <Typography variant="body2" sx={{ opacity: 0.5 }}>
            {t('Loading')}...
          </Typography>
        ) : unreimbursed.data.length === 0 ? (
          <Typography variant="body2" sx={{ opacity: 0.5 }}>
            {t('No data available')}
          </Typography>
        ) : (
          <>
            <Box
              className="flex flex-col gap-1"
              sx={{ opacity: unreimbursedLoading ? 0.5 : 1 }}
            >
              {unreimbursed.data.map((expense) => (
                <TravelExpenseRow
                  key={expense.id}
                  expense={expense}
                  colors={colors}
                  t={t}
                  editable={isEditable(expense)}
                  onEdit={() => handleEdit(expense)}
                  onDelete={() => setDeleteExpense(expense)}
                />
              ))}
            </Box>
            <Pagination
              data={unreimbursed}
              setData={setUnreimbursed}
              setLoading={setUnreimbursedLoading}
              isSmall
            />
          </>
        )}
      </Box>

      <Box className="flex flex-col gap-1 flex-1 min-h-[400px]">
        <Typography fontWeight={600}>
          {t('Travel Expense Reimbursements')}
        </Typography>
        <ExoTable
          extendElement={({ row }) => <ReimbursementDetails row={row} own={own} />}
          data={apiData}
          isLoading={isLoading}
          columns={reimbursementColumns(t)}
          columnSettingsKey="profileTravelReimbursements"
        />
      </Box>

      <ExoDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        limitWidth
        maxWidth="1250px"
      >
        {createOpen && (
          <NewTravelExpense
            userId={userId}
            onCreate={handleCreateDone}
            onCancel={() => setCreateOpen(false)}
          />
        )}
      </ExoDialog>

      <ExoDialog
        open={Boolean(editExpense)}
        onClose={() => setEditExpense(null)}
        limitWidth
        maxWidth="1250px"
      >
        {editExpense && (
          <NewTravelExpense
            travelExpense={editExpense}
            userId={editExpense.userId}
            onCreate={handleEditDone}
            onCancel={() => setEditExpense(null)}
          />
        )}
      </ExoDialog>

      <ExoAlert
        open={Boolean(deleteExpense)}
        onClose={() => setDeleteExpense(null)}
        onSubmit={handleDelete}
        alert={{
          type: 'warning',
          submitLabel: t('Delete'),
          message: t(
            'Are you sure you want to remove this travel expense? The original request will be declined.'
          ),
        }}
      />
    </Box>
  );
};

const reimbursementColumns = (t) => [
  {
    header: (
      <Tooltip title={t('Billed')}>
        <PriceCheckIcon />
      </Tooltip>
    ), // header of column
    key: 'openAmount', // key of column in row-object
    thAlign: 'left', // header text alignment
    tdAlign: 'left', // cell text alignment
    width: '33px', // width of column
    cell: ({ row }) => (
      <Box>{Number(row.openAmount) === 0 && <PriceCheckIcon />}</Box>
    ),
  },
  {
    header: (
      <Tooltip title={t('Document Present')}>
        <RequestPageIcon />
      </Tooltip>
    ), // header of column
    key: 'file', // key of column in row-object
    thAlign: 'left', // header text alignment
    tdAlign: 'left', // cell text alignment
    width: '33px', // width of column
    cell: ({ row }) => <Box>{row.fileId && <RequestPageIcon />}</Box>,
  },
  {
    header: t('Period'), // header of column
    key: 'startDate', // key of column in row-object
    thAlign: 'left', // header text alignment
    tdAlign: 'left', // cell text alignment
    sortable: true, // enable sort function for column
    cell: ({ row }) => (
      <Box className="monospace whitespace-nowrap">
        <DateLabel date={row.startDate} /> - <DateLabel date={row.endDate} />
      </Box>
    ), // custom xml for cells in the column. you get the row as object
  },
  {
    header: t('Trips'), // header of column
    key: 'tripCount', // key of column in row-object
    thAlign: 'right', // header text alignment
    tdAlign: 'right', // cell text alignment
    width: '70px', // width of column
    hiddenMobile: true,
  },
  {
    header: t('Brutto'), // header of column
    key: 'amount', // key of column in row-object
    thAlign: 'right', // header text alignment
    tdAlign: 'right', // cell text alignment
    sortable: true, // enable sort function for column
    width: '130px', // width of column
    cell: ({ row }) => (
      <Box className="monospace">
        <MoneyLabel money={row.amount} />
      </Box>
    ), // custom xml for cells in the column. you get the row as object
  },
];

/**
 * Read-only detail view for the profile's Travel section: the generated
 * document (with preview and download) and every report the reimbursement
 * pays out — no editing, no transaction handling. The own endpoint delivers
 * the reports inline; the admin index does not, so the detail endpoint is
 * fetched on expand there.
 */
const ReimbursementDetails = ({ row: listRow, own }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { apiData } = useFetch(
    own ? null : `travel-expense-reimbursements/${listRow.id}`
  );
  const row = own ? listRow : apiData?.data;

  if (!row) {
    return (
      <Typography variant="body2" sx={{ opacity: 0.5 }}>
        {t('Loading')}...
      </Typography>
    );
  }

  return (
    <Box className="flex flex-col gap-4 w-full">
      <CardGrid>
        <FilePreviewCard file={row.file} />
        <CardContainer span={2}>
          <Box className="flex flex-col gap-1 w-full px-2">
            <Box className="flex flex-row gap-2 items-center justify-between">
              <Typography variant="body2" className="opacity-70">
                {t('Period')}
              </Typography>
              <Typography variant="body2" component="div" className="monospace">
                <DateLabel date={row.startDate} /> -{' '}
                <DateLabel date={row.endDate} />
              </Typography>
            </Box>
            <Box className="flex flex-row gap-2 items-center justify-between">
              <Typography variant="body2" className="opacity-70">
                {t('Trips')}
              </Typography>
              <Typography variant="body2">
                {(row.travelExpenses || []).length}
              </Typography>
            </Box>
            <Box className="flex flex-row gap-2 items-center justify-between">
              <Typography variant="body2" className="opacity-70">
                {t('Brutto Amount')}
              </Typography>
              <Typography variant="body2" component="div">
                <MoneyLabel money={row.amount} />
              </Typography>
            </Box>
          </Box>
        </CardContainer>
      </CardGrid>

      {(row.travelExpenses || []).map((trip) => (
        <Box
          key={trip.id}
          className="flex flex-col gap-2 rounded-lg p-3"
          sx={{
            backgroundColor: colors.bgInput + '60',
            border: `1px solid ${colors.selected}`,
          }}
        >
          <Box className="flex items-center gap-2 flex-wrap">
            <Typography variant="h6">
              {trip.project?.name || t('Travel Expense')}
            </Typography>
            <Typography variant="body2" className="opacity-60 monospace">
              <DateLabel date={trip.startTime} />
            </Typography>
            <Typography className="ml-auto monospace font-bold">
              <MoneyLabel money={trip.totalPrice} />
            </Typography>
          </Box>
          <TravelExpenseRequestDetails
            request={{ travelExpense: trip, comment: trip.comment }}
          />
        </Box>
      ))}
    </Box>
  );
};

const TravelExpenseStatus = ({ status, t }) => {
  switch (status) {
    case 'draft':
      return <Status text={t('Draft')} variant="draft" fullWidth={false} />;
    case 'submitted':
      return <Status text={t('Submitted')} variant="open" fullWidth={false} />;
    case 'approved':
      return <Status text={t('Approved')} variant="accepted" fullWidth={false} />;
    case 'declined':
      return <Status text={t('Declined')} variant="rejected" fullWidth={false} />;
    default:
      return <></>;
  }
};

const TravelExpenseRow = ({ expense, colors, t, editable, onEdit, onDelete }) => {
  const address = expense.destinationAddress;
  const addressLine1 = address
    ? [address.street, address.houseNumber].filter(Boolean).join(' ')
    : '';
  const addressLine2 = address
    ? [address.zipCode, address.city].filter(Boolean).join(' ')
    : '';

  const projectName = expense.project?.name;

  const metaParts = [];
  if (expense.distanceKilometers != null) {
    metaParts.push(`${numberToString(expense.distanceKilometers, 2)} km`);
  }
  if (expense.durationHours != null && expense.durationHours !== '') {
    metaParts.push(`${numberToString(expense.durationHours, 2)} h`);
  }

  return (
    <Box
      className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5"
      sx={{ border: `1px solid ${colors.selected}` }}
    >
      <Box className="flex items-center gap-4 min-w-0">
        <MeansOfTransportationIcon value={expense.meansOfTransportation} />
        <Box className="flex flex-col min-w-0">
          <Box className="flex items-center gap-2 flex-wrap">
            <Typography variant="body2">
              <DateLabel date={expense.startTime} />
            </Typography>
            {projectName && (
              <Typography
                fontSize={11}
                sx={{
                  px: 0.75,
                  borderRadius: '6px',
                  backgroundColor: colors.selected,
                  opacity: 0.9,
                }}
              >
                {projectName}
              </Typography>
            )}
          </Box>
          {metaParts.length > 0 && (
            <Typography fontSize={10} sx={{ opacity: 0.6 }}>
              {metaParts.join(' · ')}
            </Typography>
          )}
          {(addressLine1 || addressLine2) && (
            <Typography fontSize={10} sx={{ opacity: 0.6 }}>
              {[addressLine1, addressLine2].filter(Boolean).join(', ')}
            </Typography>
          )}
        </Box>
      </Box>
      <Box className="flex items-center gap-1 shrink-0">
        <TravelExpenseStatus status={expense.status} t={t} />
        <Typography className="monospace">
          <MoneyLabel money={expense.totalPrice} />
        </Typography>
        {editable && (
          <>
            <Tooltip title={t('Edit')}>
              <IconButton size="small" onClick={onEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('Delete')}>
              <IconButton size="small" color="error" onClick={onDelete}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeTravelExpenseReimbursements;
